import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from '@shared/core';
import { useEventUserRole } from '@shared/components/AuthProvider';
import type { GuestSiteQueryParams } from './useGuestSiteQueryParams';
import { useGuestSiteQueryParams } from './useGuestSiteQueryParams';
import { useGuestSitePageViewedDuration } from './useGuestSitePageViewedDuration';
import { getPageInfo, getSlugFromPathname } from './getPageInfo';

export { getPageInfo, getSlugFromPathname };

export const useGuestSitePageChanged = (pages: ReadonlyArray<EventPageFragment>, isMigratedCustomPageEnabled?: (slug: string) => boolean) => {
  const telemetry = useGuestSiteTelemetry() as unknown as Record<string, { enter: (role: string, args: GuestSiteQueryParams) => void }>;
  const [currentPageSlug, setCurrentPageSlug] = useState<string>();
  const { isAdmin } = useEventUserRole();
  const history = useHistory();
  const queryParams = useGuestSiteQueryParams();
  const { trackPageVisitDuration } = useGuestSitePageViewedDuration(pages, isMigratedCustomPageEnabled);

  const trackPage = useCallback(
    locationPathName => {
      const { pageSlug, pageFound } = getPageInfo(pages, locationPathName, { isMigratedCustomPageEnabled });
      if (pageFound && pageSlug && telemetry[pageSlug] && currentPageSlug !== pageFound.pageSlug) {
        telemetry[pageSlug].enter(isAdmin ? 'admin' : 'guest', { ...queryParams, slug: pageFound.pageSlug });
        setCurrentPageSlug(pageFound.pageSlug);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, history, currentPageSlug, isMigratedCustomPageEnabled],
  );

  useEffect(() => {
    if (history.location.pathname) {
      trackPage(history.location.pathname);
      trackPageVisitDuration(history.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);
};
